/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import type {SentryTeamName} from '@stripe-internal/connect-embedded-lib';
import {FetchApiError} from '@stripe-internal/connect-embedded-lib';
import type {IAnalytics, IReports, Tags} from '@sail/observability';
import type {PrimitiveRecord, ReportErrorTags} from '../utils/errorHandling';
import type {MetaOptions} from '../connect/ConnectJSInterface/InitAndUpdateOptionsTypes';
import {getUSE_FOR_ANALYTICS_ONLY_HostApp} from '../utils/getHostApp';
import {
  reportRequestError,
  reportRequestSuccess,
} from '../utils/telemetry/connectElementAnalyticsSender';
import type {AuthenticationState} from '../data-layer-frame/authentication/types';
import type {
  AccountSessionClaim,
  RiskSignalsCollectionDataLayerResponse,
} from '../data-layer-frame/types';
import type {ReducedMetaOptions} from '../connect/ConnectJsOptions';

export async function withErrorReportingAndAnalytics<
  Params,
  Returns extends
    | AccountSessionClaim
    | AuthenticationState
    | RiskSignalsCollectionDataLayerResponse,
>(
  // Note: we allow reports to be null, as sentry error reporting isn't always available
  reports: IReports | null,
  metaOptions: MetaOptions | ReducedMetaOptions,
  analytics: IAnalytics,
  analyticsId: string,
  func: (args: Params) => Promise<Returns>,
  args: Params,
  extraErrorTags?: Record<string, string>,
  meta?: string,
  owner?: SentryTeamName,
  expectErrors: string[] = [],
  mutedErrors: string[] = [],
): Promise<Returns> {
  const hostApp = getUSE_FOR_ANALYTICS_ONLY_HostApp(metaOptions);
  const startTime = window.performance.now();

  let response: Returns;
  try {
    response = await func(args);
    reportRequestSuccess(
      analytics,
      analyticsId,
      Math.ceil(window.performance.now() - startTime),
      owner,
      undefined,
      undefined,
      !!metaOptions?.disableAnalyticsInStripeDomain,
    );
  } catch (error) {
    if (
      error instanceof FetchApiError &&
      error.error.httpStatus === 401 &&
      !error.error.code
    ) {
      error.error.code = 'reauth_required';
    }

    const reportError = reports
      ? (
          error: Error,
          tags?: ReportErrorTags,
          extra?: PrimitiveRecord,
          owner?: SentryTeamName,
        ) => {
          reports.error(error, {
            project: owner,
            tags: {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              ...(tags as Tags | undefined),
              ...extraErrorTags,
            },
            extras: extra,
          });
        }
      : null;

    reportRequestError(
      analytics,
      reportError,
      analyticsId,
      error,
      hostApp,
      expectErrors,
      mutedErrors,
      meta,
      Math.ceil(window.performance.now() - startTime),
      undefined,
      owner,
      undefined,
      !!metaOptions?.disableAnalyticsInStripeDomain,
    );
    throw error;
  }

  return response;
}
